// react
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// types
import {
	Account,
	Activity,
	AdvanceTotal,
	CashInMethod,
	CashInTotal,
	Category,
	Component,
	IfrBankBalancePayload,
	IfrForecastDeductionPayload,
	IfrForecastPayload,
	IfrPlan,
	IfrReconciliationItemPayload,
	IfrTables,
	Loan,
	PaletteBrand,
	ReconciliationItem,
	Transaction,
} from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';

// context
import { ContextIfrForecast, ContextIfrReconciliation } from './Ifr.context';
import { ContextIfr, ContextProject } from '../Project.context';

// hooks
import { useFetch } from '../../../../hooks/useFetch';
import { useIfr } from '../../../../hooks/useIfr';
import { useIfrPrint } from '../../../../hooks/useIfrPrint';

// pages
import { Details } from './Details/Details';

// mui
import { BoxProps, Container, Grid, Typography } from '@mui/material';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';

// ui
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { Loading } from '../../../ui/Loading/Loading';
import { Tabs } from '../../../ui/Tabs/Tabs';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// dialogs
import { DialogEditIfr } from '../../../dialogs/DialogEditIfr/DialogEditIfr';
import { DialogTransactions } from '../../../dialogs/DialogTransactions/DialogTransactions';
import { DialogUpdateIfrStatus } from '../../../dialogs/DialogUpdateIfrStatus/DialogUpdateIfrStatus';

// utils
import { getAllocation } from '../../../../utils/allocations';
import { formatDate } from '../../../../utils/dates';
import { cellWidths } from '../../../../utils/ifrs';

// theme
import { theme } from '../../../../theme';

// props
const columnTypes = [
	'Accumulated',
	'Allocation',
	'Paid',
	'Plan',
	'Utilization',
	'Variation',
] as const;
type ColumnType = (typeof columnTypes)[number];

export type GridColDefIfr = GridColDef & {
	loan?: Loan;
};

export interface GetColumnsRowsProps {
	accounts?: Account[];
	cashInMethods?: CashInMethod[];
	categories?: Category[];
	components?: Component[];
	palette: PaletteBrand;
}

export interface GetColumnsRowsBalancesProps {
	cashIn?: IfrTables['cashIn'];
	cashOutCategories?: IfrTables['cashOutCategories'];
	ifrTables?: IfrTables;
	openingBalances?: IfrTables['openingBalances'];
	type: 'CLOSING' | 'OPENING';
}

export interface GetColumnsRowsReconciliationProps {
	cashIn?: IfrTables['cashIn'];
	cashOutCategories?: IfrTables['cashOutCategories'];
	openingBalances?: IfrTables['openingBalances'];
	reconItems: ReconciliationItem[];
}

export interface GetColumnsRowsForecastProps {
	cashIn?: IfrTables['cashIn'];
	cashOutCategories?: IfrTables['cashOutCategories'];
	openingBalances?: IfrTables['openingBalances'];
}

export interface GetColumnsRowsReimbursementsProps {
	categories?: Category[];
	components?: Component[];
	palette: PaletteBrand;
}

// constants
const columnTypesColors: Record<ColumnType, PaletteBrand> = {
	Accumulated: 'orange',
	Allocation: 'magenta',
	Plan: 'blue',
	Paid: 'orange',
	Variation: 'yellow',
	Utilization: 'magenta',
};

export const Ifr = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { calculateLoanClosingCashBalance, handleExportCsv, handleExportPdf, handleExportXls } =
		useIfr();
	const { handlePrintIfr } = useIfrPrint();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogIfr = searchParams.get('dialogIfr');

	// context
	const { ifr, ifrPayload, ifrTables, isLoadingIfr, setIfrPayload, setIfrTables, refetchIfr } =
		useContext(ContextIfr);
	const { project } = useContext(ContextProject);

	// state
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [cashInMethods, setCashInMethods] = useState<CashInMethod[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [reconItems, setReconItems] = useState<ReconciliationItem[]>([]);
	const [tabValue, setTabValue] = useState('1a');
	const [valuesForecast, setValuesForecast] = useState<Record<string, Record<string, number>>>({});
	const [valuesReconciliation, setValuesReconciliation] = useState<
		Record<string, Record<string, number>>
	>({});

	// refs
	const ifrTablesRef = useRef<IfrTables>();

	// handlers
	const handlePrintIfrCallback = useCallback(() => {
		if (ifr && ifrTablesRef.current) {
			handlePrintIfr({
				ifr,
				ifrTables: ifrTablesRef.current,
				project,
			});
		}
	}, [handlePrintIfr, ifr, project]);

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogAccount');
		searchParams.delete('dialogCashInMethod');
		searchParams.delete('dialogCashOutAccumulatedDateEnd');
		searchParams.delete('dialogCashOutDateEnd');
		searchParams.delete('dialogCashOutDateStart');
		searchParams.delete('dialogCategory');
		searchParams.delete('dialogComponent');
		searchParams.delete('dialogDateEnd');
		searchParams.delete('dialogDateStart');
		searchParams.delete('dialogLoan');
		searchParams.delete('dialogStatus');
		searchParams.delete('dialogTransactions');
		setSearchParams(searchParams);
	};

	// vars
	const ifrDateEndFormatted = formatDate({
		date: ifr?.dateEnd,
		format: 'YYYY-MM-DD',
	});
	const ifrDateStartFormatted = formatDate({
		date: ifr?.dateStart,
		format: 'YYYY-MM-DD',
	});
	const ifrDateBalancesFormatted = formatDate({
		date: new Date(
			// get account balances as of the day before the IFR start date
			new Date(ifrDateStartFormatted).setDate(new Date(ifrDateStartFormatted).getDate() - 1)
		),
		format: 'YYYY-MM-DD',
	});

	let ifrDateFinalized: Date | string | undefined = (ifr?.activities as Activity[])?.find(
		(a) => a.type === 'IFR_FINALIZED'
	)?.created;

	if (ifrDateFinalized) {
		ifrDateFinalized = new Date(ifrDateFinalized);
	}

	const loansEnabled = loans.filter((loan) => loan.status === 'ENABLED');
	const hasEnabledLoans = loansEnabled.length > 0;

	// fetch loans
	const { fetchRequest: fetchLoans } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&hasTransactions=true&fields=code,id,status`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch accounts
	const { fetchRequest: fetchAccounts } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&fields=advances,advancesAccumulated,balances,currency,id,name&advancesCurrency=${ifr?.currency}&advancesAccumulatedDateEnd=${ifrDateEndFormatted}&advancesDateStart=${ifrDateStartFormatted}&advancesDateEnd=${ifrDateEndFormatted}&balancesCurrencies=${ifr?.currency}&dateBalances=${ifrDateBalancesFormatted}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setAccounts(res.data);
			}
		},
	});

	// fetch cash in methods
	const { fetchRequest: fetchCashInMethods } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/cash-in-methods?project=${project?.id},null&cashInCurrency=${ifr?.currency}&cashInAccumulatedDateEnd=${ifrDateEndFormatted}&cashInDateStart=${ifrDateStartFormatted}&cashInDateEnd=${ifrDateEndFormatted}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashInMethods(res.data);
			}
		},
	});

	// fetch categories
	const { fetchRequest: fetchCategories } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&hasTransactions=true&cashOutCurrency=${ifr?.currency}&cashOutAccumulatedDateEnd=${ifrDateEndFormatted}&cashOutDateStart=${ifrDateStartFormatted}&cashOutDateEnd=${ifrDateEndFormatted}&populate=allocations.conversions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCategories(res.data);
			}
		},
	});

	// fetch components
	const { fetchRequest: fetchComponents } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&hasTransactions=true&cashOutCurrency=${ifr?.currency}&cashOutAccumulatedDateEnd=${ifrDateEndFormatted}&cashOutDateStart=${ifrDateStartFormatted}&cashOutDateEnd=${ifrDateEndFormatted}&populate=allocations.conversions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComponents(res.data);
			}
		},
	});

	// fetch reconciliation items
	const { fetchRequest: fetchReconItems } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/reconciliation-items?project=null,${project?.id || ''}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setReconItems(res.data);
			}
		},
	});

	// vars
	const tabs = useMemo(() => {
		return [
			{
				component: RouterLink,
				label: `1A ${t('categories')}`,
				to: `categories`,
				value: 'categories',
			},
			{
				component: RouterLink,
				label: `1B ${t('components')}`,
				to: `components`,
				value: 'components',
			},
			{
				component: RouterLink,
				label: `1C ${t('reconciliation')}`,
				to: `reconciliation`,
				value: 'reconciliation',
			},
			{
				component: RouterLink,
				label: `1D ${t('forecast')}`,
				to: `forecast`,
				value: 'forecast',
			},
		];
	}, [t]);

	const getTabByLocation = useCallback(
		(pathname) => {
			if (pathname.includes('categories')) {
				return 'categories';
			} else if (pathname.includes('components')) {
				return 'components';
			} else if (pathname.includes('forecast')) {
				return 'forecast';
			} else if (pathname.includes('reconciliation')) {
				return 'reconciliation';
			} else {
				return tabs?.[0]?.value;
			}
		},
		[tabs]
	);

	const getColumnsRowsReconciliation = useCallback(
		({
			cashIn,
			cashOutCategories,
			openingBalances,
			reconItems,
		}: GetColumnsRowsReconciliationProps) => {
			const loanColumns: GridColDefIfr[] = loans.map((loan) => ({
				flex: 1,
				field: `loan${loan.id}`,
				headerName: loan.code,
				loan,
				minWidth: cellWidths.sm,
			}));

			const columns: GridColDefIfr[] = [
				{
					field: 'name',
					headerName: t('name'),
					minWidth: cellWidths.lg,
				},
				...loanColumns,
				{
					field: 'total',
					headerName: t('total'),
					minWidth: cellWidths.sm,
				},
			];

			// helper functions for row calculations
			const getTotalValue = (fieldId: string, values: Record<string, Record<string, number>>) => {
				let total = 0;
				if (values[fieldId] !== undefined) {
					total = Object.values(values[fieldId]).reduce((acc, value) => {
						return acc + (Number(value) || 0);
					}, 0);
				}
				return total;
			};

			const getTotalForLoan = (id: number, values: Record<string, Record<string, number>>) => {
				let total = 0;
				Object.keys(values).forEach((key) => {
					if (!['bank-balance', 'differences'].includes(key)) {
						if (values[key][`loan${id}`]) {
							total += values[key][`loan${id}`];
						}
					}
				});
				return total;
			};

			// vars
			const palette: keyof typeof theme.palette.brand = 'purple';
			const fieldsSticky = {
				left: ['name'],
			};
			const boxPropsName = {
				sx: {
					justifyContent: 'flex-start',
				},
			};

			const boxPropsFill = {
				sx: {
					backgroundColor: theme.palette.brand[palette][100],
					color: theme.palette.brand[palette][800],
				},
			};

			const boxPropsGrey = {
				sx: {
					backgroundColor: theme.palette.brand.grey[200],
				},
			};

			const headers: GridValidRowModel = {
				fieldsSticky,
				id: 'headers',
				name: null,
				total: t('total'),
			};

			// Build values from valuesReconciliation state (user input) merged with ifr data
			const values: Record<string, Record<string, number>> = {};

			// Initialize bank-balance and differences
			if (!values['bank-balance']) {
				values['bank-balance'] = {};
			}
			if (!values.differences) {
				values.differences = {};
			}

			const dataClosingBalances: Record<string, number | undefined> = {};
			const dataDifferences: Record<string, number> = {};
			const dataLoansBankBalances: Record<string, number | undefined> = {};
			const dataTotals: Record<string, number> = {};
			const dataUnexplained: Record<string, number> = {};
			const propsLoans: Record<string, BoxProps> = {};
			const propsLoansDark: Record<string, BoxProps> = {};
			const propsRecon: Record<string, BoxProps> = {
				name: boxPropsName,
				total: boxPropsFill,
			};
			const propsStatic: Record<string, BoxProps> = {
				name: {
					sx: {
						...boxPropsName.sx,
						...boxPropsGrey.sx,
					},
				},
				total: {
					sx: {
						...boxPropsFill.sx,
					},
				},
			};

			loans.forEach((loan) => {
				const loanField = `loan${loan.id}`;

				// define box props
				propsLoans[loanField] = {
					sx: {
						backgroundColor: theme.palette.brand[palette][100],
					},
				};
				propsRecon[loanField] = {
					sx: {
						justifyContent: 'flex-end',
					},
				};
				propsLoansDark[loanField] = {
					sx: {
						...boxPropsFill.sx,
						justifyContent: 'flex-end',
					},
				};
				propsStatic[loanField] = {
					sx: {
						...boxPropsGrey.sx,
						justifyContent: 'flex-end',
					},
				};

				// calculate closing balance
				const loanClosingCashBalance =
					calculateLoanClosingCashBalance({
						cashIn,
						cashOutCategories,
						loanId: loan.id,
						openingBalances,
					}) || 0;

				// use bank balance from valuesReconciliation if exists, otherwise from ifr
				const loanBankBalance =
					valuesReconciliation?.['bank-balance']?.[loanField] ??
					ifr?.bankBalances?.find((b) => b.loan === loan.id)?.amount ??
					0;

				// set bank balances
				values['bank-balance'][loanField] = loanBankBalance;

				// calculate differences
				values.differences[loanField] = Number(loanClosingCashBalance - loanBankBalance);

				// define row data
				dataClosingBalances[loanField] = loanClosingCashBalance;
				dataLoansBankBalances[loanField] = values['bank-balance']?.[loanField];
				dataDifferences[loanField] = values.differences?.[loanField];

				// define row header
				headers[loanField] = loan.code;
			});

			// set recon items - use valuesReconciliation if exists, otherwise from ifr
			reconItems.forEach((item) => {
				const reconItemKey = `recon-item-${item.id}`;
				values[reconItemKey] = {};
				loans.forEach((loan) => {
					const loanField = `loan${loan.id}`;
					values[reconItemKey][loanField] =
						valuesReconciliation?.[reconItemKey]?.[loanField] ??
						ifr?.reconciliationItems?.find(
							(i) => i.reconciliationItem === item.id && i.loan === loan.id
						)?.amount ??
						0;
				});
			});

			// calculate totals after values are set
			loans.forEach((loan) => {
				const loanField = `loan${loan.id}`;
				dataTotals[loanField] = getTotalForLoan(loan.id, values);
				dataUnexplained[loanField] = dataDifferences[loanField] - dataTotals[loanField];
			});

			const propsTotalRow = {
				name: {
					sx: {
						...boxPropsName.sx,
						...boxPropsFill.sx,
					},
				},
				...propsLoansDark,
				total: boxPropsFill,
			};

			headers.BoxProps = {
				name: {
					sx: {
						...boxPropsFill.sx,
						...boxPropsName.sx,
					},
				},
				total: boxPropsFill,
				...propsLoans,
			};

			const editable: string[] =
				ifr?.status === 'DRAFT' ? loans.map((loan) => `loan${loan.id}`) : [];

			const reconItemRows: GridValidRowModel[] = reconItems.map((item) => {
				const fieldId = `recon-item-${item.id}`;
				return {
					BoxProps: propsRecon,
					fieldsEditable: editable,
					fieldsSticky,
					id: fieldId,
					name: item.name,
					reconciliationItem: item.id,
					total: getTotalValue(fieldId, values),
					...values[fieldId],
				};
			});

			const reconRows: GridValidRowModel[] = [
				{
					BoxProps: propsStatic,
					fieldsSticky,
					id: 'closing-balance',
					name: `(A) ${t('tableSheets.recon.rows.closingBalance')}`,
					...dataClosingBalances,
					total: Object.values(dataClosingBalances).reduce(
						(acc, value) => Number(acc) + (value || 0),
						0
					),
				},
				{
					BoxProps: propsRecon,
					fieldsEditable: editable,
					fieldsSticky,
					id: 'bank-balance',
					name: `(B) ${t('tableSheets.recon.rows.bankBalance', {
						date: formatDate({ date: ifr?.dateEnd ?? new Date() }),
					})}`,
					total: getTotalValue('bank-balance', values),
					...dataLoansBankBalances,
				},
				{
					BoxProps: propsStatic,
					fieldsSticky,
					id: 'difference',
					name: `(C) ${t('difference')}`,
					...dataDifferences,
					total: Object.values(dataDifferences).reduce((acc, value) => acc + (value || 0), 0),
				},
				...reconItemRows,
				{
					BoxProps: propsTotalRow,
					fieldsSticky,
					id: 'total-reconciliation',
					name: `(D) ${t('totalReconciliation')}`,
					...dataTotals,
					total: Object.values(dataTotals).reduce((acc, value) => acc + (value || 0), 0),
				},
				{
					BoxProps: propsTotalRow,
					fieldsSticky,
					id: 'unexplained',
					name: `(C-D) ${t('unexplainedDifferences')}`,
					...dataUnexplained,
					total: Object.values(dataUnexplained).reduce((acc, value) => acc + (value || 0), 0),
				},
			];

			const rows = [headers, ...reconRows];

			return { columns, palette, rows };
		},
		[calculateLoanClosingCashBalance, ifr, loans, t, valuesReconciliation]
	);

	const getColumnsRowsForecast = useCallback(
		({ cashIn, cashOutCategories, openingBalances }: GetColumnsRowsForecastProps) => {
			// helper functions
			const getColumnsForLoans = () => {
				const columns: GridColDefIfr[] = loans.map((loan) => ({
					flex: 1,
					field: `loan${loan.id}`,
					headerName: loan.code,
					loan,
					minWidth: cellWidths.sm,
				}));

				return columns;
			};

			const getTotalValue = ({ fieldId }: { fieldId: string }) => {
				let total = 0;

				// sum total values for field
				if (valuesForecast[fieldId]) {
					total = Object.values(valuesForecast[fieldId]).reduce((acc, value) => {
						return acc + (Number(value) || 0);
					}, 0);
				}

				return total;
			};

			const getTotalPaymentsExpected = ({ loanId }: { loanId: string }) => {
				const directPayment = valuesForecast['direct-payment']?.[loanId] || 0;
				const scPayments = valuesForecast['sc-payments']?.[loanId] || 0;
				const reimbursements = valuesForecast['reimbursements']?.[loanId] || 0;

				return directPayment + scPayments + reimbursements;
			};

			const getCashRequirement = ({ loanId }: { loanId: string }) => {
				const totalForecasted = valuesForecast['total-forecasted-payments']?.[loanId] || 0;
				const closingBalance = valuesForecast['closing-cash-balance']?.[loanId] || 0;
				const totalPaymentsExpected = getTotalPaymentsExpected({ loanId });

				return totalForecasted - closingBalance - totalPaymentsExpected;
			};

			const getTotalCashRequirement = () => {
				const totalForecasted = getTotalValue({ fieldId: 'total-forecasted-payments' });
				const totalClosingBalance = getTotalValue({ fieldId: 'closing-cash-balance' });
				const totalPaymentsExpected =
					getTotalValue({ fieldId: 'direct-payment' }) +
					getTotalValue({ fieldId: 'sc-payments' }) +
					getTotalValue({ fieldId: 'reimbursements' });

				return totalForecasted - totalClosingBalance - totalPaymentsExpected;
			};

			// vars
			const palette = 'blue' as const;
			const fieldsSticky = {
				left: ['name'],
			};
			const boxPropsName = {
				sx: {
					justifyContent: 'flex-start',
				},
			};

			const boxPropsFill = {
				sx: {
					backgroundColor: theme.palette.brand[palette][100],
					color: theme.palette.brand[palette][800],
				},
			};

			const boxPropsGrey = {
				sx: {
					backgroundColor: theme.palette.brand.grey[200],
				},
			};

			const boxPropsLoans: Record<string, BoxProps> = {};
			const boxPropsLoansDark: Record<string, BoxProps> = {};

			const boxPropsStatic: Record<string, BoxProps> = {
				name: {
					sx: {
						...boxPropsName.sx,
						...boxPropsGrey.sx,
					},
				},
				total: {
					sx: {
						...boxPropsFill.sx,
					},
				},
			};

			const columns: GridColDefIfr[] = [
				{
					field: 'name',
					headerName: t('name'),
					minWidth: cellWidths.lg,
				},
				...getColumnsForLoans(),
				{
					field: 'total',
					headerName: t('total'),
					minWidth: cellWidths.sm,
				},
			];

			const rowHeaders: GridValidRowModel = {
				fieldsSticky,
				id: 'headers',
				name: null,
				total: t('total'),
			};

			const boxPropsForecast: Record<string, BoxProps> = {
				name: {
					sx: {
						...boxPropsName.sx,
					},
				},
				total: {
					sx: {
						...boxPropsFill.sx,
					},
				},
			};

			const boxPropsTotalRow: Record<string, BoxProps> = {
				name: {
					sx: {
						...boxPropsName.sx,
						...boxPropsFill.sx,
					},
				},
				total: {
					sx: {
						...boxPropsFill.sx,
					},
				},
			};

			const rowDataClosingCashBalance: Record<string, number | undefined> = {};

			loans.forEach((loan) => {
				const loanField = `loan${loan.id}`;

				// define box props
				boxPropsForecast[loanField] = {
					sx: {
						justifyContent: 'flex-end',
					},
				};
				boxPropsLoans[loanField] = {
					sx: {
						backgroundColor: theme.palette.brand[palette][100],
					},
				};
				boxPropsLoansDark[loanField] = {
					sx: {
						...boxPropsFill.sx,
						justifyContent: 'flex-end',
					},
				};
				boxPropsStatic[loanField] = {
					sx: {
						...boxPropsGrey.sx,
						justifyContent: 'flex-end',
					},
				};

				boxPropsTotalRow[loanField] = {
					sx: {
						...boxPropsFill.sx,
						justifyContent: 'flex-end',
					},
				};

				// define row data
				rowDataClosingCashBalance[loanField] =
					calculateLoanClosingCashBalance({
						cashIn,
						cashOutCategories,
						loanId: loan.id,
						openingBalances,
					}) || undefined;

				// define row header
				rowHeaders[loanField] = loan.code;
			});

			rowHeaders.BoxProps = {
				name: {
					sx: {
						...boxPropsFill.sx,
						justifyContent: 'flex-start',
					},
				},
				...boxPropsLoans,
				total: {
					sx: {
						...boxPropsFill.sx,
					},
				},
			};

			// calculate dynamic row data that updates with values changes
			const rowDataTotalPaymentsExpected: Record<string, number> = {};
			const rowDataCashRequirement: Record<string, number> = {};

			loans.forEach((loan) => {
				const loanField = `loan${loan.id}`;
				rowDataTotalPaymentsExpected[loanField] = getTotalPaymentsExpected({ loanId: loanField });
				rowDataCashRequirement[loanField] = getCashRequirement({ loanId: loanField });
			});

			// recalculate totals for display
			const totalPaymentsExpectedTotal = Object.values(rowDataTotalPaymentsExpected).reduce(
				(acc, value) => acc + value,
				0
			);

			const fieldsLoans = loans.map((loan) => `loan${loan.id}`);

			let fieldsEditable: string[] = [];

			if (ifr?.status === 'DRAFT') {
				fieldsEditable = fieldsLoans;
			}

			const rowsForecast = [
				{
					BoxProps: boxPropsForecast,
					fieldsEditable,
					fieldsSticky,
					id: 'total-forecasted-payments',
					name: `(A) ${t('tableSheets.forecast.rows.totalForecastedPayments')}`,
					total: getTotalValue({ fieldId: 'total-forecasted-payments' }),
					...(valuesForecast['total-forecasted-payments'] || {}),
				},
				{
					BoxProps: boxPropsStatic,
					fieldsSticky,
					id: 'closing-cash-balance',
					name: `(B) ${t('tableSheets.forecast.rows.closingCashBalance')}`,
					...rowDataClosingCashBalance,
					total: Object.values(rowDataClosingCashBalance).reduce(
						(acc, value) => Number(acc) + (value || 0),
						0
					),
				},
				{
					BoxProps: boxPropsStatic,
					fieldsSticky,
					id: 'payments-expected-label',
					name: `(C) ${t('tableSheets.forecast.rows.paymentsExpectedLabel')}`,
					total: '',
					...Object.fromEntries(fieldsLoans.map((field) => [field, ''])),
				},
				{
					BoxProps: boxPropsForecast,
					fieldsEditable,
					fieldsSticky,
					id: 'direct-payment',
					name: t('tableSheets.forecast.rows.directPayment'),
					total: getTotalValue({ fieldId: 'direct-payment' }),
					...(valuesForecast['direct-payment'] || {}),
				},
				{
					BoxProps: boxPropsForecast,
					fieldsEditable,
					fieldsSticky,
					id: 'sc-payments',
					name: t('tableSheets.forecast.rows.scPayments'),
					total: getTotalValue({ fieldId: 'sc-payments' }),
					...(valuesForecast['sc-payments'] || {}),
				},
				{
					BoxProps: boxPropsForecast,
					fieldsEditable,
					fieldsSticky,
					id: 'reimbursements',
					name: t('tableSheets.forecast.rows.reimbursements'),
					total: getTotalValue({ fieldId: 'reimbursements' }),
					...(valuesForecast['reimbursements'] || {}),
				},
				{
					BoxProps: boxPropsTotalRow,
					fieldsSticky,
					id: 'total-payments-expected',
					name: t('tableSheets.forecast.rows.totalPaymentsExpected'),
					...rowDataTotalPaymentsExpected,
					total: totalPaymentsExpectedTotal,
				},
				{
					BoxProps: boxPropsTotalRow,
					fieldsSticky,
					id: 'cash-requirement',
					name: `(D) ${t('tableSheets.forecast.rows.cashRequirement')}`,
					...rowDataCashRequirement,
					total: getTotalCashRequirement(),
				},
			];

			const rows = [rowHeaders, ...rowsForecast];

			return { columns, palette, rows };
		},
		[calculateLoanClosingCashBalance, ifr?.status, loans, t, valuesForecast]
	);

	const getColumnsRowsBalances = useCallback(
		({
			cashIn,
			cashOutCategories,
			ifrTables,
			openingBalances,
			type,
		}: GetColumnsRowsBalancesProps) => {
			const columns: GridColDef[] = [
				{
					field: 'name',
					headerName: 'Name',
					minWidth: cellWidths.lg,
				},
			];

			// define box props
			const boxPropsLoansHeaders: Record<string, BoxProps> = {};
			const boxPropsLoansBalances: Record<string, BoxProps> = {};

			// define row data
			const rowDataHeaders: Record<string, string> = {};
			const rowDataLoans: Record<string, number | undefined> = {};

			loans.forEach((loan) => {
				const loanField = `loan${loan.id}`;

				// define columns
				columns.push({
					field: loanField,
					flex: 1,
					headerName: loan.code,
					minWidth: cellWidths.sm,
				});

				// set box props
				boxPropsLoansHeaders[loanField] = {
					sx: {
						backgroundColor: theme.palette.brand.grey[100],
					},
				};

				boxPropsLoansBalances[loanField] = {
					sx: {
						justifyContent: 'flex-end',
					},
				};

				// set row data
				rowDataHeaders[loanField] = loan.code;

				let loanBalance: number | undefined;

				if (type === 'CLOSING' && ifrTables) {
					loanBalance = calculateLoanClosingCashBalance({
						cashIn,
						cashOutCategories,
						loanId: loan.id,
						openingBalances,
					});
				} else if (type === 'OPENING') {
					loanBalance = 0;

					// get loan balance for each account in the reporting currency
					for (let i = 0; i < accounts.length; i++) {
						const account = accounts[i];
						// find balance in the IFR currency (reporting currency)
						const balanceForLoan = account.balances?.find((b) => {
							return (b.loan as Loan).id === loan.id && b.currency === ifr?.currency;
						});

						loanBalance += balanceForLoan?.balance || 0;
					}
				}
				rowDataLoans[loanField] = loanBalance;
			});

			columns.push({
				field: 'total',
				headerName: 'Total',
				minWidth: cellWidths.sm,
			});

			const rowHeaders = {
				BoxProps: {
					name: {
						sx: {
							backgroundColor: theme.palette.brand.grey[100],
						},
					},
					total: {
						sx: {
							backgroundColor: theme.palette.brand.grey[100],
						},
					},
					...boxPropsLoansHeaders,
				},
				id: 'headers',
				name: null,
				total: t('total'),
				...rowDataHeaders,
			};

			const rows = [
				rowHeaders,
				{
					BoxProps: {
						name: {
							sx: {
								backgroundColor: theme.palette.brand.grey[100],
								justifyContent: 'flex-start',
							},
						},
						total: {
							sx: {
								backgroundColor: theme.palette.brand.grey[100],
							},
						},
						...boxPropsLoansBalances,
					},
					id: type === 'CLOSING' ? 'closing-balances' : 'opening-balances',
					name: t('total'),
					...rowDataLoans,
					total:
						Object.values(rowDataLoans)
							.filter((value) => typeof value === 'number')
							.reduce((total, value) => (total || 0) + (value || 0), 0) || undefined,
				},
			];

			return { columns, rows };
		},
		[accounts, calculateLoanClosingCashBalance, ifr, loans, t]
	);

	const getColumnsRows = useCallback(
		({ accounts, cashInMethods, categories, components, palette }: GetColumnsRowsProps) => {
			// vars
			const version = (ifr?.ifrPlan as IfrPlan)?.versions?.find((v) => v.status === 'FINALIZED');

			// define IFR month range
			const ifrMonthStart = new Date(ifr?.dateStart || '').getUTCMonth() + 1;
			const ifrMonthEnd = new Date(ifr?.dateEnd || '').getUTCMonth() + 1;
			const ifrMonthNums: number[] = [];

			// handle fiscal years
			if (ifrMonthStart <= ifrMonthEnd) {
				// same calendar year
				for (let i = ifrMonthStart; i <= ifrMonthEnd; i++) {
					ifrMonthNums.push(i);
				}
			} else {
				// two calendar years
				// first part (start month to December)
				for (let i = ifrMonthStart; i <= 12; i++) {
					ifrMonthNums.push(i);
				}
				// second part (January to end month)
				for (let i = 1; i <= ifrMonthEnd; i++) {
					ifrMonthNums.push(i);
				}
			}

			// helper functions
			const getColumnsForLoans = ({ name }: { name: string }) => {
				const columns: GridColDefIfr[] = loans.map((loan, i) => ({
					colSpan: (params) => {
						if (params.id === 'section') {
							return loans.length + 1;
						}
						return 1;
					},
					field: `loan${loan.id}${name}`,
					headerName: `${loan.code} ${name}`,
					loan,
					minWidth: cellWidths.sm,
				}));

				return columns;
			};

			const createBoxProps = ({
				colorType,
				palette,
			}: {
				colorType: ColumnType;
				palette?: PaletteBrand;
			}) => {
				const color = palette || columnTypesColors[colorType];
				return {
					sx: {
						backgroundColor: theme.palette.brand[color][100],
					},
				};
			};

			const boxPropsFill = {
				sx: {
					backgroundColor: theme.palette.brand[palette][100],
					color: theme.palette.brand[palette][800],
				},
			};

			const boxPropsName = {
				sx: {
					justifyContent: 'flex-start',
				},
			};

			const boxPropsJustifyFlexEnd = {
				sx: {
					justifyContent: 'flex-end',
				},
			};

			const boxPropsTotalDark = {
				sx: {
					...boxPropsFill.sx,
					...boxPropsJustifyFlexEnd.sx,
				},
			};

			const fieldsSuffix: Record<string, string> = {
				totalUtilization: '%',
				totalVariation: '%',
			};

			const generateRowHeadersAndBoxProps = ({
				columnType,
				palette,
			}: {
				columnType: ColumnType;
				palette?: PaletteBrand;
			}) => {
				const rowData: Record<string, string> = {};
				const boxProps: Record<string, BoxProps> = {};
				const boxPropsData: Record<string, BoxProps> = {
					totalAllocation: boxPropsJustifyFlexEnd,
					totalPaid: boxPropsJustifyFlexEnd,
					totalPlan: boxPropsJustifyFlexEnd,
					totalUtilization: boxPropsJustifyFlexEnd,
					totalVariation: boxPropsJustifyFlexEnd,
				};
				const boxPropsTotal: Record<string, BoxProps> = {};

				loans.forEach((loan, i) => {
					const fieldName = `loan${loan.id}${columnType}`;
					rowData[fieldName] = loan.code;
					boxProps[fieldName] = createBoxProps({ colorType: columnType, palette });
					boxPropsData[fieldName] = boxPropsJustifyFlexEnd;
					boxPropsTotal[fieldName] = boxPropsTotalDark;

					if (['Utilization', 'Variation'].includes(columnType)) {
						fieldsSuffix[fieldName] = '%';
					}
				});

				return { rowData, boxProps, boxPropsData, boxPropsTotal };
			};

			const calculateTotalsForColumnType = ({
				rows,
				columnType,
			}: {
				rows: GridValidRowModel[];
				columnType: ColumnType;
			}) => {
				const totals: Record<string, number | undefined> = {};
				let grandTotal: number | undefined;

				loans.forEach((loan, i) => {
					const fieldName = `loan${loan.id}${columnType}`;
					const columnTotal =
						rows.reduce((sum, row) => sum + ((row[fieldName] as number) || 0), 0) || undefined;
					totals[fieldName] = columnTotal;

					if (columnType !== 'Utilization' && typeof columnTotal === 'number') {
						grandTotal = (grandTotal || 0) + columnTotal;
					}
				});

				return { totals, grandTotal };
			};

			const columns: GridColDef[] = [
				{
					field: 'name',
					headerName: 'Category',
					minWidth: cellWidths.lg,
				},
				...getColumnsForLoans({ name: 'Plan' }),
				{
					field: 'totalPlan',
					headerName: 'Total Planned',
					minWidth: cellWidths.sm,
				},
				...getColumnsForLoans({ name: 'Paid' }),
				{
					field: 'totalPaid',
					headerName: 'Total Paid',
					minWidth: cellWidths.sm,
				},
				...getColumnsForLoans({ name: 'Variation' }),
				{
					field: 'totalVariation',
					headerName: 'Total Variation',
					minWidth: cellWidths.sm,
				},
				...getColumnsForLoans({ name: 'Accumulated' }),
				{
					field: 'totalAccumulated',
					headerName: 'Total Accumulated',
					minWidth: cellWidths.sm,
				},
				...getColumnsForLoans({ name: 'Allocation' }),
				{
					field: 'totalAllocation',
					headerName: 'Total Allocation',
					minWidth: cellWidths.sm,
				},
				...getColumnsForLoans({ name: 'Utilization' }),
				{
					field: 'totalUtilization',
					headerName: 'Total Utilization',
					minWidth: cellWidths.sm,
				},
			];

			// generate row data and box props for all column types
			const planData = generateRowHeadersAndBoxProps({ columnType: 'Plan', palette });
			const paidData = generateRowHeadersAndBoxProps({ columnType: 'Paid' });
			const variationData = generateRowHeadersAndBoxProps({ columnType: 'Variation' });
			const accumulatedData = generateRowHeadersAndBoxProps({ columnType: 'Accumulated' });
			const allocationData = generateRowHeadersAndBoxProps({ columnType: 'Allocation' });
			const utilizationData = generateRowHeadersAndBoxProps({ columnType: 'Utilization' });

			const boxPropsData = {
				name: boxPropsName,
				...accumulatedData.boxPropsData,
				...allocationData.boxPropsData,
				...paidData.boxPropsData,
				...planData.boxPropsData,
				...utilizationData.boxPropsData,
				...variationData.boxPropsData,
			};

			const fieldsSticky = {
				left: ['name'],
			};

			const rowSections: GridValidRowModel = {
				BoxProps: {
					name: {
						sx: {
							...boxPropsFill.sx,
						},
					},
					...planData.boxProps,
					...paidData.boxProps,
					...variationData.boxProps,
					...accumulatedData.boxProps,
					...allocationData.boxProps,
					...utilizationData.boxProps,
				},
				fieldsSticky,
				id: 'section',
				[`loan${loans?.[0]?.id}Plan`]: t('planned'),
				[`loan${loans?.[0]?.id}Paid`]: t('paid'),
				[`loan${loans?.[0]?.id}Variation`]: t('variation'),
				[`loan${loans?.[0]?.id}Accumulated`]: t('accumulated'),
				[`loan${loans?.[0]?.id}Allocation`]: t('allocation'),
				[`loan${loans?.[0]?.id}Utilization`]: t('utilization'),
				name: null,
			};

			const rowHeaders: GridValidRowModel = {
				BoxProps: {
					name: {
						sx: {
							...boxPropsFill.sx,
						},
					},
					...planData.boxProps,
					totalPlan: createBoxProps({ colorType: 'Plan', palette }),
					...paidData.boxProps,
					totalPaid: createBoxProps({ colorType: 'Paid' }),
					...variationData.boxProps,
					totalVariation: createBoxProps({ colorType: 'Variation' }),
					...accumulatedData.boxProps,
					totalAccumulated: createBoxProps({ colorType: 'Accumulated' }),
					...allocationData.boxProps,
					totalAllocation: createBoxProps({ colorType: 'Allocation' }),
					...utilizationData.boxProps,
					totalUtilization: createBoxProps({ colorType: 'Utilization' }),
				},
				fieldsSticky,
				id: 'headers',
				name: null,
				...planData.rowData,
				totalPlan: t('total'),
				...paidData.rowData,
				totalPaid: t('total'),
				...variationData.rowData,
				totalVariation: t('total'),
				...accumulatedData.rowData,
				totalAccumulated: t('total'),
				...allocationData.rowData,
				totalAllocation: t('total'),
				...utilizationData.rowData,
				totalUtilization: t('total'),
			};

			let rowsData: GridValidRowModel[] = [];

			// add accounts row data
			if (accounts) {
				// define row data for loans
				const rowDataLoans = {} as Record<string, number | undefined>;

				const rowsAccounts = accounts
					?.sort((a, b) => a.name.localeCompare(b.name))
					.map((account) => {
						const fieldsClickable: string[] = [];

						// define data by account for IFR months
						const dataForAccount = version?.data?.filter(
							(d) => d.account === account.id && ifrMonthNums.includes(d.month)
						);

						// define totals
						let totalAccumulated: number | undefined;
						let totalPaid: number | undefined;
						let totalPlan: number | undefined;
						let totalVariation: number | undefined;

						// for each loan
						loans.forEach((loan, i) => {
							// define account data for loan
							const dataForLoan = dataForAccount?.filter((d) => d.loan === loan.id);

							// define advances for loan
							const accountAdvancesForLoan = account.advances?.filter((a) => a.loan === loan.id);

							// define accumulated advances for loan
							const accountAdvancesAccumulatedForLoan = account.advancesAccumulated?.filter(
								(a: AdvanceTotal) => a.loan === loan.id
							);

							// define row values
							const rowLoanAccumulatedAmount = accountAdvancesAccumulatedForLoan?.length
								? accountAdvancesAccumulatedForLoan.reduce(
										(sum: number, a: AdvanceTotal) => sum + (a.total || 0),
										0
									)
								: undefined;
							const rowLoanPaidAmount = accountAdvancesForLoan?.length
								? accountAdvancesForLoan.reduce((sum, a) => sum + (a.total || 0), 0)
								: undefined;
							const rowLoanPlanAmount = dataForLoan?.length
								? dataForLoan.reduce((sum, d) => sum + (d.amount || 0), 0)
								: undefined;
							const rowLoanVariation =
								rowLoanPlanAmount && rowLoanPaidAmount
									? Number(
											(((rowLoanPlanAmount - rowLoanPaidAmount) / rowLoanPlanAmount) * 100).toFixed(
												2
											)
										)
									: undefined;

							// append row data
							rowDataLoans[`loan${loan.id}Accumulated`] = rowLoanAccumulatedAmount;
							rowDataLoans[`loan${loan.id}Paid`] = rowLoanPaidAmount;
							rowDataLoans[`loan${loan.id}Plan`] = rowLoanPlanAmount;
							rowDataLoans[`loan${loan.id}Variation`] = rowLoanVariation;

							// add loan plan amount to total plan amount
							if (typeof rowLoanPlanAmount === 'number') {
								totalPlan = (totalPlan || 0) + rowLoanPlanAmount;
							}

							// add loan accumulated amount to total accumulated amount
							if (typeof rowLoanAccumulatedAmount === 'number') {
								totalAccumulated = (totalAccumulated || 0) + rowLoanAccumulatedAmount;

								if (rowLoanAccumulatedAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Accumulated`);
								}
							}

							// add loan paid amount to total paid amount
							if (typeof rowLoanPaidAmount === 'number') {
								totalPaid = (totalPaid || 0) + rowLoanPaidAmount;

								if (rowLoanPaidAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Paid`);
								}
							}

							// add loan variation to total variation amount
							if (rowLoanVariation) {
								totalVariation = (totalVariation || 0) + rowLoanVariation;
							}
						});

						// calculate total variation as percentage based on total plan and total paid
						if (totalPlan && totalPaid) {
							totalVariation = Number((((totalPlan - totalPaid) / totalPlan) * 100).toFixed(2));
						} else {
							totalVariation = undefined;
						}

						const rowData: GridValidRowModel = {
							account,
							BoxProps: boxPropsData,
							fieldsClickable,
							fieldsSticky,
							fieldsSuffix,
							id: `account-${account.id}`,
							name: t('advancesTo', { name: account.name }),
							...rowDataLoans,
							totalAccumulated,
							totalPaid,
							totalPlan,
							totalVariation,
						};
						return rowData;
					});

				rowsData = [...rowsData, ...rowsAccounts];
			}

			// add cash in methods row data
			if (cashInMethods) {
				// define row data for loans
				const rowDataLoans = {} as Record<string, number | undefined>;

				const rowsCashInMethods = cashInMethods
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((method) => {
						const fieldsClickable: string[] = [];

						// define data by cash in method for IFR months
						const dataForCashInMethod = version?.data?.filter(
							(d) => d.cashInMethod === method.id && ifrMonthNums.includes(d.month)
						);

						// define totals
						let totalAccumulated: number | undefined;
						let totalPaid: number | undefined;
						let totalPlan: number | undefined;
						let totalVariation: number | undefined;

						// for each loan
						loans.forEach((loan, i) => {
							// define cash in method data for loan
							const dataForLoan = dataForCashInMethod?.filter((d) => d.loan === loan.id);

							// define row values
							const rowLoanAccumulatedAmount =
								method.cashInAccumulated?.find((c: CashInTotal) => c.loan === loan.id)?.total ||
								undefined;
							const rowLoanPaidAmount =
								method.cashIn?.find((c) => c.loan === loan.id)?.total || undefined;
							const rowLoanPlanAmount = dataForLoan?.length
								? dataForLoan.reduce((sum, d) => sum + (d.amount || 0), 0)
								: undefined;
							const rowLoanVariation =
								rowLoanPlanAmount && rowLoanPaidAmount
									? Number(
											(((rowLoanPlanAmount - rowLoanPaidAmount) / rowLoanPlanAmount) * 100).toFixed(
												2
											)
										)
									: undefined;

							// append row data
							rowDataLoans[`loan${loan.id}Accumulated`] = rowLoanAccumulatedAmount;
							rowDataLoans[`loan${loan.id}Paid`] = rowLoanPaidAmount;
							rowDataLoans[`loan${loan.id}Plan`] = rowLoanPlanAmount;
							rowDataLoans[`loan${loan.id}Variation`] = rowLoanVariation;

							// add loan accumulated amount to total accumulated amount
							if (typeof rowLoanAccumulatedAmount === 'number') {
								totalAccumulated = (totalAccumulated || 0) + rowLoanAccumulatedAmount;

								if (rowLoanAccumulatedAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Accumulated`);
								}
							}

							// add loan paid amount to total paid amount
							if (typeof rowLoanPaidAmount === 'number') {
								totalPaid = (totalPaid || 0) + rowLoanPaidAmount;

								if (rowLoanPaidAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Paid`);
								}
							}

							// add loan plan amount to total plan amount
							if (typeof rowLoanPlanAmount === 'number') {
								totalPlan = (totalPlan || 0) + rowLoanPlanAmount;
							}

							// add loan variation to total variation amount
							if (rowLoanVariation) {
								totalVariation = (totalVariation || 0) + rowLoanVariation;
							}
						});

						// calculate total variation as percentage based on total plan and total paid
						if (totalPlan && totalPaid) {
							totalVariation = Number((((totalPlan - totalPaid) / totalPlan) * 100).toFixed(2));
						} else {
							totalVariation = undefined;
						}

						const rowData: GridValidRowModel = {
							BoxProps: boxPropsData,
							cashInMethod: method,
							fieldsClickable,
							fieldsSticky,
							fieldsSuffix,
							id: `cash-in-method-${method.id}`,
							name: method.name,
							...rowDataLoans,
							totalAccumulated,
							totalPaid,
							totalPlan,
							totalVariation,
						};
						return rowData;
					});

				rowsData = [...rowsData, ...rowsCashInMethods];
			}

			// add categories row data
			if (categories) {
				// define row data for loans
				const rowDataLoans = {} as Record<string, number | undefined>;

				const rowsCategories = categories
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((category) => {
						const fieldsClickable: string[] = [];

						// define data by category for IFR months
						const dataForCategory = version?.data?.filter(
							(d) => d.category === category.id && ifrMonthNums.includes(d.month)
						);

						// define totals
						let totalAccumulated: number | undefined;
						let totalAllocation: number | undefined;
						let totalPaid: number | undefined;
						let totalPlan: number | undefined;
						let totalUtilization: number | undefined;
						let totalVariation: number | undefined;

						// for each loan
						loans.forEach((loan, i) => {
							// define category data by loan
							const dataForLoan = dataForCategory?.filter((d) => d.loan === loan.id);
							const cashOutForLoan = category?.cashOut?.find((a) => a.loan === loan.id);
							const cashOutAccumulatedForLoan = category?.cashOutAccumulated?.find(
								(a) => a.loan === loan.id
							);

							// define row values
							let rowLoanAccumulatedAmount = undefined;
							if (cashOutAccumulatedForLoan) {
								rowLoanAccumulatedAmount =
									cashOutAccumulatedForLoan.total +
									cashOutAccumulatedForLoan.totalApplied -
									cashOutAccumulatedForLoan.totalReimbursed;
							}

							const rowLoanPlanAmount = dataForLoan?.length
								? dataForLoan.reduce((sum, d) => sum + (d.amount || 0), 0)
								: undefined;
							const rowLoanAllocationAmount = category?.allocations
								? getAllocation({
										allocations: category.allocations,
										currency: ifr?.currency,
										loanId: loan.id,
									})?.amount
								: undefined;

							let rowLoanPaidAmount = undefined;
							if (cashOutForLoan) {
								rowLoanPaidAmount =
									cashOutForLoan.total +
									cashOutForLoan.totalApplied -
									cashOutForLoan.totalReimbursed;
							}

							const rowLoanUtilization =
								rowLoanAccumulatedAmount && rowLoanAllocationAmount
									? Number(
											(((rowLoanAccumulatedAmount || 0) / rowLoanAllocationAmount) * 100).toFixed(2)
										)
									: undefined;
							const rowLoanVariation =
								rowLoanPlanAmount && rowLoanPaidAmount
									? Number(
											(((rowLoanPlanAmount - rowLoanPaidAmount) / rowLoanPlanAmount) * 100).toFixed(
												2
											)
										)
									: undefined;

							// append row data
							rowDataLoans[`loan${loan.id}Accumulated`] = rowLoanAccumulatedAmount;
							rowDataLoans[`loan${loan.id}Allocation`] = rowLoanAllocationAmount;
							rowDataLoans[`loan${loan.id}Paid`] = rowLoanPaidAmount;
							rowDataLoans[`loan${loan.id}Plan`] = rowLoanPlanAmount;
							rowDataLoans[`loan${loan.id}Utilization`] = rowLoanUtilization;
							rowDataLoans[`loan${loan.id}Variation`] = rowLoanVariation;

							// add loan accumulated amount to total accumulated amount
							if (typeof rowLoanAccumulatedAmount === 'number') {
								totalAccumulated = (totalAccumulated || 0) + rowLoanAccumulatedAmount;

								if (rowLoanAccumulatedAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Accumulated`);
								}
							}

							// add loan cash out amount to total paid amount
							if (rowLoanPaidAmount) {
								totalPaid = (totalPaid || 0) + rowLoanPaidAmount;

								if (rowLoanPaidAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Paid`);
								}
							}

							// add loan plan amount to total plan amount
							if (rowLoanPlanAmount) {
								totalPlan = (totalPlan || 0) + rowLoanPlanAmount;
							}

							// add loan allocation amount to total allocation amount
							if (rowLoanAllocationAmount) {
								totalAllocation = (totalAllocation || 0) + rowLoanAllocationAmount;
							}

							// add loan variance to total variance amount
							if (rowLoanVariation) {
								totalVariation = (totalVariation || 0) + rowLoanVariation;
							}
						});

						// calculate total utilization as percentage based on total accumulated and total allocation
						if (totalAccumulated && totalAllocation) {
							totalUtilization = Number(((totalAccumulated / totalAllocation) * 100).toFixed(2));
						} else {
							totalUtilization = undefined;
						}

						// calculate total variation as percentage based on total plan and total paid
						if (totalPlan && totalPaid) {
							totalVariation = Number((((totalPlan - totalPaid) / totalPlan) * 100).toFixed(2));
						} else {
							totalVariation = undefined;
						}

						const rowData: GridValidRowModel = {
							BoxProps: boxPropsData,
							category,
							fieldsClickable,
							fieldsSticky,
							fieldsSuffix,
							id: `category-${category.id}`,
							name: category.name,
							...rowDataLoans,
							totalAccumulated,
							totalAllocation,
							totalPaid,
							totalPlan,
							totalUtilization,
							totalVariation,
						};
						return rowData;
					});

				rowsData = [...rowsData, ...rowsCategories];
			}

			// add components row data
			if (components) {
				const fieldsClickable: string[] = [];

				// define row data for components
				const rowDataLoans = {} as Record<string, number | undefined>;

				const rowsComponents = components
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((component) => {
						// define data by component for IFR months
						const dataForComponent = version?.data?.filter(
							(d) => d.component === component.id && ifrMonthNums.includes(d.month)
						);

						// define totals
						let totalAccumulated: number | undefined;
						let totalAllocation: number | undefined;
						let totalPaid: number | undefined;
						let totalPlan: number | undefined;
						let totalUtilization: number | undefined;
						let totalVariation: number | undefined;

						// for each loan
						loans.forEach((loan, i) => {
							// define category data by loan
							const dataForLoan = dataForComponent?.filter((d) => d.loan === loan.id);
							const cashOutForLoan = component?.cashOut?.find((a) => a.loan === loan.id);
							const cashOutAccumulatedForLoan = component?.cashOutAccumulated?.find(
								(a) => a.loan === loan.id
							);

							// define row values
							let rowLoanAccumulatedAmount = undefined;
							if (cashOutAccumulatedForLoan) {
								rowLoanAccumulatedAmount =
									cashOutAccumulatedForLoan.total +
									cashOutAccumulatedForLoan.totalApplied -
									cashOutAccumulatedForLoan.totalReimbursed;
							}
							const rowLoanPlanAmount = dataForLoan?.length
								? dataForLoan.reduce((sum, d) => sum + (d.amount || 0), 0)
								: undefined;
							const rowLoanAllocationAmount = component?.allocations
								? getAllocation({
										allocations: component.allocations,
										currency: ifr?.currency,
										loanId: loan.id,
									})?.amount
								: undefined;

							let rowLoanPaidAmount = undefined;
							if (cashOutForLoan) {
								rowLoanPaidAmount =
									cashOutForLoan.total +
									cashOutForLoan.totalApplied -
									cashOutForLoan.totalReimbursed;
							}
							const rowLoanUtilization =
								rowLoanAccumulatedAmount && rowLoanAllocationAmount
									? Number(
											(((rowLoanAccumulatedAmount || 0) / rowLoanAllocationAmount) * 100).toFixed(2)
										)
									: undefined;
							const rowLoanVariation =
								rowLoanPlanAmount && rowLoanPaidAmount
									? Number(
											(((rowLoanPlanAmount - rowLoanPaidAmount) / rowLoanPlanAmount) * 100).toFixed(
												2
											)
										)
									: undefined;

							// append row data
							rowDataLoans[`loan${loan.id}Accumulated`] = rowLoanAccumulatedAmount;
							rowDataLoans[`loan${loan.id}Allocation`] = rowLoanAllocationAmount;
							rowDataLoans[`loan${loan.id}Paid`] = rowLoanPaidAmount;
							rowDataLoans[`loan${loan.id}Plan`] = rowLoanPlanAmount;
							rowDataLoans[`loan${loan.id}Utilization`] = rowLoanUtilization;
							rowDataLoans[`loan${loan.id}Variation`] = rowLoanVariation;

							// add loan accumulated amount to total accumulated amount
							if (typeof rowLoanAccumulatedAmount === 'number') {
								totalAccumulated = (totalAccumulated || 0) + rowLoanAccumulatedAmount;

								if (rowLoanAccumulatedAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Accumulated`);
								}
							}

							// add loan cash out amount to total paid amount
							if (rowLoanPaidAmount) {
								totalPaid = (totalPaid || 0) + rowLoanPaidAmount;

								if (rowLoanPaidAmount !== 0) {
									fieldsClickable.push(`loan${loan.id}Paid`);
								}
							}

							// add loan plan amount to total plan amount
							if (rowLoanPlanAmount) {
								totalPlan = (totalPlan || 0) + rowLoanPlanAmount;
							}

							// add loan allocation amount to total allocation amount
							if (rowLoanAllocationAmount) {
								totalAllocation = (totalAllocation || 0) + rowLoanAllocationAmount;
							}

							// add loan variance to total variance amount
							if (rowLoanVariation) {
								totalVariation = (totalVariation || 0) + rowLoanVariation;
							}
						});

						// calculate total utilization as percentage based on total accumulated and total allocation
						if (totalAccumulated && totalAllocation) {
							totalUtilization = Number(((totalAccumulated / totalAllocation) * 100).toFixed(2));
						} else {
							totalUtilization = undefined;
						}

						// calculate total variation as percentage based on total plan and total paid
						if (totalPlan && totalPaid) {
							totalVariation = Number((((totalPlan - totalPaid) / totalPlan) * 100).toFixed(2));
						} else {
							totalVariation = undefined;
						}

						const rowData: GridValidRowModel = {
							BoxProps: boxPropsData,
							component: component,
							fieldsClickable,
							fieldsSticky,
							fieldsSuffix,
							id: `component-${component.id}`,
							name: component.name,
							...rowDataLoans,
							totalAccumulated,
							totalAllocation,
							totalPaid,
							totalPlan,
							totalUtilization,
							totalVariation,
						};
						return rowData;
					});

				rowsData = [...rowsData, ...rowsComponents];
			}

			// calculate totals for all column types
			const accumulatedTotals = calculateTotalsForColumnType({
				rows: rowsData,
				columnType: 'Accumulated',
			});
			const allocationTotals = calculateTotalsForColumnType({
				rows: rowsData,
				columnType: 'Allocation',
			});
			const paidTotals = calculateTotalsForColumnType({ rows: rowsData, columnType: 'Paid' });
			const planTotals = calculateTotalsForColumnType({ rows: rowsData, columnType: 'Plan' });
			const utilizationTotals = calculateTotalsForColumnType({
				rows: rowsData,
				columnType: 'Utilization',
			});
			const variationTotals = calculateTotalsForColumnType({
				rows: rowsData,
				columnType: 'Variation',
			});
			// calculate utilization and variation percentages for each loan column
			loans.forEach((loan) => {
				// calculate utilization percentage
				const utilizationFieldName = `loan${loan.id}Utilization`;
				const accumulatedFieldName = `loan${loan.id}Accumulated`;
				const allocationFieldName = `loan${loan.id}Allocation`;
				const accumulatedValue = accumulatedTotals.totals[accumulatedFieldName];
				const allocationValue = allocationTotals.totals[allocationFieldName];
				if (accumulatedValue && allocationValue) {
					utilizationTotals.totals[utilizationFieldName] = Number(
						((accumulatedValue / allocationValue) * 100).toFixed(2)
					);
				} else {
					utilizationTotals.totals[utilizationFieldName] = undefined;
				}

				// calculate variation percentage
				const variationFieldName = `loan${loan.id}Variation`;
				const planFieldName = `loan${loan.id}Plan`;
				const paidFieldName = `loan${loan.id}Paid`;
				const planValue = planTotals.totals[planFieldName];
				const paidValue = paidTotals.totals[paidFieldName];
				if (planValue && paidValue) {
					variationTotals.totals[variationFieldName] = Number(
						(((planValue - paidValue) / planValue) * 100).toFixed(2)
					);
				} else {
					variationTotals.totals[variationFieldName] = undefined;
				}
			});
			// calculate grand total utilization percentage
			if (accumulatedTotals.grandTotal && allocationTotals.grandTotal) {
				utilizationTotals.grandTotal = Number(
					((accumulatedTotals.grandTotal / allocationTotals.grandTotal) * 100).toFixed(2)
				);
			}
			// calculate grand total variation percentage
			if (planTotals.grandTotal && paidTotals.grandTotal) {
				variationTotals.grandTotal = Number(
					(((planTotals.grandTotal - paidTotals.grandTotal) / planTotals.grandTotal) * 100).toFixed(
						2
					)
				);
			}
			const rowDataLoansTotals = {
				...accumulatedTotals.totals,
				...allocationTotals.totals,
				...paidTotals.totals,
				...planTotals.totals,
				...utilizationTotals.totals,
				...variationTotals.totals,
			};

			const rowTotals: GridValidRowModel = {
				BoxProps: {
					name: {
						sx: {
							...boxPropsFill.sx,
							...boxPropsName.sx,
						},
					},
					...accumulatedData.boxPropsTotal,
					totalAccumulated: boxPropsTotalDark,
					...allocationData.boxPropsTotal,
					totalAllocation: boxPropsTotalDark,
					...paidData.boxPropsTotal,
					totalPaid: boxPropsTotalDark,
					...planData.boxPropsTotal,
					totalPlan: boxPropsTotalDark,
					...utilizationData.boxPropsTotal,
					totalUtilization: boxPropsTotalDark,
					...variationData.boxPropsTotal,
					totalVariation: boxPropsTotalDark,
				},
				fieldsSticky,
				fieldsSuffix,
				id: 'totals-columns',
				...rowDataLoansTotals,
				name: t('total'),
				totalAccumulated: accumulatedTotals.grandTotal,
				totalAllocation: allocationTotals.grandTotal,
				totalPaid: paidTotals.grandTotal,
				totalPlan: planTotals.grandTotal,
				totalUtilization: utilizationTotals.grandTotal,
				totalVariation: variationTotals.grandTotal,
			};

			const rows: GridValidRowModel[] = [rowSections, rowHeaders, ...rowsData, rowTotals];

			return { columns, palette, rows };
		},
		[ifr, loans, t]
	);

	const getColumnsRowsReimbursements = useCallback(
		({ categories, components, palette }: GetColumnsRowsReimbursementsProps) => {
			const items: (Category | Component)[] = (categories || components || []) as (
				| Category
				| Component
			)[];

			const columns: GridColDefIfr[] = [
				{
					field: 'name',
					headerName: t('name'),
					minWidth: cellWidths.lg,
				},
			];

			// define box props
			const boxPropsLoanHeaders: Record<string, BoxProps> = {};
			const boxPropsLoanData: Record<string, BoxProps> = {};

			// define row data
			const rowDataHeaders: Record<string, string> = {};
			const rowDataLoans: Record<string, number | string | undefined> = {};

			loans.forEach((loan) => {
				const loanField = `loan${loan.id}`;

				// define columns
				columns.push({
					field: loanField,
					flex: 1,
					headerName: loan.code,
					loan,
					minWidth: cellWidths.sm,
				});

				// set box props
				boxPropsLoanHeaders[loanField] = {
					sx: {
						backgroundColor: theme.palette.brand[palette][100],
					},
				};
				boxPropsLoanData[loanField] = {
					sx: {
						justifyContent: 'flex-end',
					},
				};

				// set row data
				rowDataHeaders[loanField] = loan.code;

				// sum totalApplied - totalReimbursed across all items for this loan
				let loanTotal: number | undefined;
				items.forEach((item) => {
					const cashOutForLoan = item.cashOut?.find((a) => a.loan === loan.id);
					if (cashOutForLoan) {
						loanTotal =
							(loanTotal || 0) + (cashOutForLoan.totalApplied - cashOutForLoan.totalReimbursed);
					}
				});

				rowDataLoans[loanField] = Number(loanTotal?.toFixed(2));
			});

			columns.push({
				field: 'total',
				headerName: t('total'),
				minWidth: cellWidths.sm,
			});

			const rowHeaders: GridValidRowModel = {
				BoxProps: {
					name: {
						sx: {
							backgroundColor: theme.palette.brand[palette][100],
						},
					},
					total: {
						sx: {
							backgroundColor: theme.palette.brand[palette][100],
						},
					},
					...boxPropsLoanHeaders,
				},
				id: 'headers',
				name: null,
				total: t('total'),
				...rowDataHeaders,
			};

			const totalGrand: number | string | undefined = Object.values(rowDataLoans)
				.filter((value): value is number => typeof value === 'number')
				.reduce((total, value) => (total || 0) + (value || 0), 0);

			const rows: GridValidRowModel[] = [
				rowHeaders,
				{
					BoxProps: {
						name: {
							sx: {
								backgroundColor: theme.palette.brand[palette][100],
								justifyContent: 'flex-start',
							},
						},
						total: {
							sx: {
								backgroundColor: theme.palette.brand[palette][100],
							},
						},
						...boxPropsLoanData,
					},
					id: 'reimbursements',
					name: t('total'),
					...rowDataLoans,
					total: totalGrand,
				},
			];

			return { columns, palette, rows };
		},
		[loans, t]
	);

	// fetch loans when project changes
	useEffect(() => {
		if (project?.id) {
			fetchLoans();
		}
	}, [fetchLoans, project]);

	// fetch data when ifr changes
	useEffect(() => {
		if (project?.id && ifr) {
			fetchAccounts();
			fetchCashInMethods();
			fetchCategories();
			fetchComponents();
			fetchReconItems();
		}
	}, [
		fetchAccounts,
		fetchCashInMethods,
		fetchCategories,
		fetchComponents,
		fetchReconItems,
		ifr,
		project,
	]);

	// set ifr tables
	useEffect(() => {
		setIfrTables((ifrTablesOld) => {
			const ifrTablesNew = { ...ifrTablesOld };

			const {
				columns: cashInColumns,
				palette: cashInPalette,
				rows: cashInRows,
			} = getColumnsRows({
				accounts,
				cashInMethods,
				palette: 'green',
			});

			const {
				columns: cashOutCategoriesColumns,
				palette: cashOutCategoriesPalette,
				rows: cashOutCategoriesRows,
			} = getColumnsRows({
				categories,
				palette: 'teal',
			});

			const {
				columns: cashOutComponentsColumns,
				palette: cashOutComponentsPalette,
				rows: cashOutComponentsRows,
			} = getColumnsRows({
				components: components,
				palette: 'teal',
			});

			const { columns: openingBalancesColumns, rows: openingBalancesRows } = getColumnsRowsBalances(
				{
					type: 'OPENING',
				}
			);

			ifrTablesNew.cashIn = {
				columns: cashInColumns,
				palette: cashInPalette,
				rows: cashInRows,
			};

			ifrTablesNew.cashOutCategories = {
				columns: cashOutCategoriesColumns,
				palette: cashOutCategoriesPalette,
				rows: cashOutCategoriesRows,
			};

			ifrTablesNew.cashOutComponents = {
				columns: cashOutComponentsColumns,
				palette: cashOutComponentsPalette,
				rows: cashOutComponentsRows,
			};

			ifrTablesNew.openingBalances = {
				columns: openingBalancesColumns,
				palette: 'grey',
				rows: openingBalancesRows,
			};

			const {
				columns: reimbursementCategoriesColumns,
				palette: reimbursementCategoriesPalette,
				rows: reimbursementCategoriesRows,
			} = getColumnsRowsReimbursements({
				categories,
				palette: 'grey',
			});

			ifrTablesNew.reimbursementCategories = {
				columns: reimbursementCategoriesColumns,
				palette: reimbursementCategoriesPalette,
				rows: reimbursementCategoriesRows,
			};

			const {
				columns: reimbursementComponentsColumns,
				palette: reimbursementComponentsPalette,
				rows: reimbursementComponentsRows,
			} = getColumnsRowsReimbursements({
				components,
				palette: 'grey',
			});

			ifrTablesNew.reimbursementComponents = {
				columns: reimbursementComponentsColumns,
				palette: reimbursementComponentsPalette,
				rows: reimbursementComponentsRows,
			};

			const { columns: closingBalancesColumns, rows: closingBalancesRows } = getColumnsRowsBalances(
				{
					cashIn: ifrTablesNew.cashIn,
					cashOutCategories: ifrTablesNew.cashOutCategories,
					ifrTables: ifrTablesNew,
					openingBalances: ifrTablesNew.openingBalances,
					type: 'CLOSING',
				}
			);

			ifrTablesNew.closingBalances = {
				columns: closingBalancesColumns,
				palette: 'grey',
				rows: closingBalancesRows,
			};

			const {
				columns: reconciliationColumns,
				palette: reconciliationPalette,
				rows: reconciliationRows,
			} = getColumnsRowsReconciliation({
				cashIn: ifrTablesNew.cashIn,
				cashOutCategories: ifrTablesNew.cashOutCategories,
				openingBalances: ifrTablesNew.openingBalances,
				reconItems,
			});

			ifrTablesNew.reconciliation = {
				columns: reconciliationColumns,
				palette: reconciliationPalette,
				rows: reconciliationRows,
			};

			const {
				columns: forecastColumns,
				palette: forecastPalette,
				rows: forecastRows,
			} = getColumnsRowsForecast({
				cashIn: ifrTablesNew.cashIn,
				cashOutCategories: ifrTablesNew.cashOutCategories,
				openingBalances: ifrTablesNew.openingBalances,
			});

			ifrTablesNew.forecast = {
				columns: forecastColumns,
				palette: forecastPalette,
				rows: forecastRows,
			};

			return ifrTablesNew;
		});
	}, [
		accounts,
		cashInMethods,
		categories,
		components,
		getColumnsRows,
		getColumnsRowsBalances,
		getColumnsRowsForecast,
		getColumnsRowsReconciliation,
		getColumnsRowsReimbursements,
		loans,
		reconItems,
		setIfrTables,
		valuesForecast,
		valuesReconciliation,
	]);

	// set active tab when location changes
	useEffect(() => {
		const tabValueByLocation = getTabByLocation(location.pathname);
		setTabValue(tabValueByLocation);
	}, [getTabByLocation, location]);

	// keep ifrTablesRef current
	useEffect(() => {
		ifrTablesRef.current = ifrTables;
	}, [ifrTables]);

	// populate valuesReconciliation from ifr data
	useEffect(() => {
		if (ifr && reconItems.length > 0) {
			const newValues: Record<string, Record<string, number>> = {};
			newValues['bank-balance'] = {};
			newValues.differences = {};

			// set bank balances from ifr
			loans.forEach((loan) => {
				const loanField = `loan${loan.id}`;
				const loanBankBalance = ifr.bankBalances?.find((b) => b.loan === loan.id)?.amount || 0;
				newValues['bank-balance'][loanField] = loanBankBalance;
			});

			// set reconciliation items from ifr
			reconItems.forEach((item) => {
				newValues[`recon-item-${item.id}`] = {};
				loans.forEach((loan) => {
					newValues[`recon-item-${item.id}`][`loan${loan.id}`] =
						ifr.reconciliationItems?.find(
							(i) => i.reconciliationItem === item.id && i.loan === loan.id
						)?.amount || 0;
				});
			});

			setValuesReconciliation(newValues);
		}
	}, [ifr, loans, reconItems]);

	// populate valuesForecast from ifr data
	useEffect(() => {
		if (ifr && loans?.length) {
			setValuesForecast((valuesOld) => {
				const valuesNew = { ...valuesOld };

				// initialize forecast values
				valuesNew['total-forecasted-payments'] = {};
				valuesNew['closing-cash-balance'] = {};
				valuesNew['direct-payment'] = {};
				valuesNew['sc-payments'] = {};
				valuesNew['reimbursements'] = {};

				loans.forEach((loan) => {
					const loanField = `loan${loan.id}`;

					// initialize default values for each loan
					valuesNew['total-forecasted-payments'][loanField] = 0;
					valuesNew['closing-cash-balance'][loanField] =
						calculateLoanClosingCashBalance({
							cashIn: ifrTablesRef.current?.cashIn,
							cashOutCategories: ifrTablesRef.current?.cashOutCategories,
							loanId: loan.id,
							openingBalances: ifrTablesRef.current?.openingBalances,
						}) || 0;
					valuesNew['direct-payment'][loanField] = 0;
					valuesNew['sc-payments'][loanField] = 0;
					valuesNew['reimbursements'][loanField] = 0;

					// Load existing forecast data from ifr
					if (ifr.forecasts) {
						const existingForecast = ifr.forecasts.find((f) => f.loan === loan.id);
						if (existingForecast) {
							valuesNew['total-forecasted-payments'][loanField] = existingForecast.amount;
						}
					}

					// Load existing forecast deduction data from ifr
					if (ifr.forecastDeductions) {
						ifr.forecastDeductions.forEach((deduction) => {
							if (deduction.loan === loan.id) {
								switch (deduction.transactionType) {
									case 'DIRECT':
										valuesNew['direct-payment'][loanField] = deduction.amount;
										break;
									case 'SPECIAL':
										valuesNew['sc-payments'][loanField] = deduction.amount;
										break;
									case 'REIMBURSEMENT':
										valuesNew['reimbursements'][loanField] = deduction.amount;
										break;
								}
							}
						});
					}
				});
				return valuesNew;
			});
		}
	}, [calculateLoanClosingCashBalance, ifr, loans]);

	// initialize ifrPayload
	useEffect(() => {
		if (ifr && !ifrPayload) {
			const bankBalancesPayload: IfrBankBalancePayload[] = ifr.bankBalances?.map((b) => {
				return {
					amount: b.amount,
					currency: b.currency,
					loan: b.loan,
				};
			});

			const forecastsPayload: IfrForecastPayload[] = ifr.forecasts?.map((f) => {
				return {
					amount: f.amount,
					currency: f.currency,
					loan: f.loan,
				};
			});

			const forecastDeductionsPayload: IfrForecastDeductionPayload[] = ifr.forecastDeductions?.map(
				(f) => {
					return {
						amount: f.amount,
						currency: f.currency,
						loan: f.loan,
						transactionType: f.transactionType,
					};
				}
			);

			const reconciliationItemsPayload: IfrReconciliationItemPayload[] =
				ifr.reconciliationItems.map((r) => {
					return {
						amount: r.amount,
						currency: r.currency,
						loan: r.loan,
						reconciliationItem: r.reconciliationItem,
					};
				});

			setIfrPayload({
				bankBalances: bankBalancesPayload,
				forecasts: forecastsPayload,
				forecastDeductions: forecastDeductionsPayload,
				reconciliationItems: reconciliationItemsPayload,
			});
		}
	}, [ifr, ifrPayload, setIfrPayload]);

	if (isLoadingIfr) {
		return <Loading variant="page" />;
	} else if (!ifr) {
		return <></>;
	}

	let content = (
		<Grid item xs={12}>
			<Container maxWidth={false}>
				<CardEmpty
					empty={{
						actions: [
							{
								children: t('seeFinancingSources'),
								component: RouterLink,
								to: `/project/${project?.idWbg}/financing`,
								variant: 'contained',
							},
						],
						content: {
							children: t('empty.ifr.content'),
						},
						IconCircleProps: {
							color: 'grey',
							type: 'ifr',
						},
						title: {
							children: t('empty.ifr.title'),
						},
					}}
				/>
			</Container>
		</Grid>
	);

	if (hasEnabledLoans) {
		content = (
			<>
				<Grid item xs={12}>
					<Container maxWidth={false}>
						<Details />
					</Container>
				</Grid>
				<Grid item xs={12}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Tabs
								hasBorder={false}
								tabs={tabs}
								onChange={(e, v) => setTabValue(v)}
								value={tabValue}
							/>
						</Grid>
						<Grid item xs={12}>
							<Outlet />
						</Grid>
					</Grid>
				</Grid>
			</>
		);
	}

	return (
		<ContextIfrReconciliation.Provider
			value={{
				setValues: setValuesReconciliation,
				values: valuesReconciliation,
			}}>
			<ContextIfrForecast.Provider
				value={{
					setValues: setValuesForecast,
					values: valuesForecast,
				}}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Container maxWidth={false}>
							<Grid
								alignItems="flex-end"
								container
								justifyContent={{ md: 'space-between' }}
								spacing={2}>
								<Grid item xs={12} md={true}>
									<Grid container spacing={0.5}>
										<Grid item xs={12}>
											<Grid alignItems="center" container spacing={1}>
												<Grid item>
													<Typography variant="h2">{ifr.name}</Typography>
												</Grid>
												<Grid item>
													<ChipStatus status={ifr.status} />
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12} md="auto">
									<BtnSplit
										actions={[
											{
												children: t('exportXls'),
												onClick: () => {
													handleExportXls({ ifrTables, project });
												},
												variant: 'outlined',
											},
											{
												children: t('exportPdf'),
												onClick: () => {
													handleExportPdf({ ifrTables, project });
												},
												variant: 'outlined',
											},
											{
												children: t('exportCsv'),
												onClick: () => {
													handleExportCsv({ ifrTables, project });
												},
												variant: 'outlined',
											},
											{
												children: t('print'),
												onClick: handlePrintIfrCallback,
												variant: 'outlined',
											},
										]}
										disabled={!hasEnabledLoans}
										size="small"
										variant="outlined"
									/>
								</Grid>
							</Grid>
						</Container>
					</Grid>
					{content}
				</Grid>
				<DialogUpdateIfrStatus
					isOpen={paramDialog === 'updateIfrStatus'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						// refetch ifr
						refetchIfr();
					}}
				/>
				<DialogEditIfr
					isOpen={paramDialog === 'editIfr' && paramDialogIfr === String(ifr.id)}
					onClose={handleCloseDialog}
					onSuccess={() => {
						// refetch ifr
						refetchIfr();
					}}
				/>
				<DialogTransactions
					currency={ifr.currency}
					disablePortal={true}
					isOpen={paramDialog === 'dialogTransactions'}
					onClose={handleCloseDialog}
					TableBasicTransactionsProps={{
						currency: ifr.currency,
						getRowBgColor: (item: Transaction) => {
							// highlight transactions created after ifr finalized date
							if (ifrDateFinalized && item.created && new Date(item.created) > ifrDateFinalized) {
								return 'yellow';
							}
							return 'transparent';
						},
						hasConversions: true,
					}}
				/>
			</ContextIfrForecast.Provider>
		</ContextIfrReconciliation.Provider>
	);
};
